import React, { createContext, useContext } from 'react';

/**
 * The surface kit draws two vocabularies:
 *
 *   hard (default)  the 2026-09 events + clubs redesign — 2px black borders, square corners,
 *                   black-fill active states;
 *   soft            the Portal vocabulary of Events Home, Host Event, Clubs, People and (Sept 23)
 *                   the event page — borderless white panels on #F5F5F5, pill controls, rounded
 *                   callouts and bands, the rainbow ring for the active state.
 *
 * A page opts in once with `<SoftSurface>`; every kit component inside reads `useSoft()`. The
 * hard look stays the default so nothing else on the site changes. Ring + palette per the
 * event-page handoff §6.
 */
const SurfaceStyleContext = createContext(false);

export function SoftSurface({ children, on = true }) {
  return <SurfaceStyleContext.Provider value={on}>{children}</SurfaceStyleContext.Provider>;
}

/** True inside a SoftSurface (or when a component is told `soft` explicitly). */
export function useSoft(override) {
  const ctx = useContext(SurfaceStyleContext);
  return override === undefined || override === null ? ctx : !!override;
}

export const RING = 'linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D,#FD199F)';
/** A black pill with the ring as its border: `border: 2px solid transparent` + this background. */
export const RING_ON = `linear-gradient(#000,#000) padding-box, ${RING} border-box`;
export const RING_ON_WHITE = `linear-gradient(#fff,#fff) padding-box, ${RING} border-box`;

export const SOFT = {
  canvas: '#F5F5F5',
  surface: '#FFFFFF',
  ink: '#000000',
  muted: '#6B7280',
  faint: '#9CA3AF',
  body: '#374151',
  chip: '#F2F2F2',
  magenta: '#FF00FF',
  hotPink: '#FF00AA',
  fundTrack: '#FBD5FB',
  cyan: '#00FFFF',
  green: '#16A34A',
  freePill: '#22C55E',
  red: '#DC2626',
  redTint: '#FEE2E2',
  amber: '#B45309',
  amberTint: '#FFF8EA',
  hostWanted: '#FACC15',
  lilac: '#B5B1FF',
  listed: '#E5E7EB',
};

/** Label type roles of the soft vocabulary (§6: 9–12px / 700 / .14–.18em uppercase). */
export const SOFT_TYPE = {
  label12: { fontSize: 12, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' },
  label11: { fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' },
  label10: { fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' },
  label9: { fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' },
  note9: { fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#9CA3AF' },
  fine: { fontSize: 11, lineHeight: 1.6, color: '#6B7280' },
};
